import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'walden-trace-app/config/environment';

export default class SessionService extends Service {
  @service('router') declare routerService: RouterService;

  _msalInstance: IPublicClientApplication | null = null;

  @tracked accessToken?: string;
  @tracked guestToken?: string;
  @tracked tokenExpires?: Date | null;

  async msalInstance() {
    if (this._msalInstance) {
      return this._msalInstance;
    }

    this._msalInstance = await PublicClientApplication.createPublicClientApplication(
      this.msalConfig,
    );
    return this._msalInstance;
  }

  get msalConfig() {
    return {
      auth: {
        authority: config.msal.authority,
        clientId: config.msal.clientId,
        navigateToLoginRequestUrl: false,
        postLogoutRedirectUri: config.msal.postLogoutRedirectUri,
        redirectUri: `${window.location.origin}${config.msal.redirectUri}`,
      },
    };
  }

  get authRequest() {
    return {
      scopes: [`${config.msal.scope}`],
    };
  }

  get attemptedUrlKey() {
    return config.modulePrefix + (config.environment === 'test' ? '-test' : '') + 'attempted-url';
  }

  get accessTokenValid() {
    return !!this.accessToken && !!this.tokenExpires && this.tokenExpires > new Date();
  }

  async isAuthenticated() {
    return Boolean((await this.state())?.username);
  }

  get isGuest() {
    return !!this.guestToken && !this.accessToken;
  }

  async state(): Promise<AccountInfo | null> {
    const instance = await this.msalInstance();

    return instance.getActiveAccount();
  }

  setAttemptedUrl(url: string) {
    window.localStorage.setItem(this.attemptedUrlKey, url);
  }

  async redirectToAttemptedURL() {
    const attemptedUrl = window.localStorage.getItem(this.attemptedUrlKey) || config.rootURL;

    const routeInfo = this.routerService.recognize(attemptedUrl);

    const redirectRoute = routeInfo?.name.startsWith('app.') ? attemptedUrl : 'app';
    window.localStorage.removeItem(this.attemptedUrlKey);
    await this.routerService.replaceWith(redirectRoute);
  }

  async redirectToLogin() {
    const instance = await this.msalInstance();

    try {
      await instance.loginRedirect(this.authRequest);
    } catch (error: unknown) {
      if (isMsalInteractionInProgressError(error)) {
        sessionStorage.removeItem('msal.interaction.status');
        await instance.loginRedirect(this.authRequest);
        return;
      }
      throw error;
    }
  }

  async redirectToLoginIfNotAuthenticated(): Promise<void> {
    if (await this.isAuthenticated()) {
      return;
    }

    this.setAttemptedUrl(window.location.pathname + window.location.search);

    const instance = await this.msalInstance();

    try {
      await instance.loginRedirect(this.authRequest);
    } catch (error: unknown) {
      if (!isMsalInteractionInProgressError(error)) {
        throw error;
      }

      sessionStorage.removeItem('msal.interaction.status');

      try {
        await instance.loginRedirect(this.authRequest);
      } catch (retryError: unknown) {
        if (!isMsalInteractionInProgressError(retryError)) {
          throw retryError;
        }

        this.clearMsalSessionState();
        window.location.reload();
      }
    }
  }

  private clearMsalSessionState(): void {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('msal.'))
      .forEach((key) => sessionStorage.removeItem(key));
  }

  async validateCallbackAndRedirectToAttemptedUrl() {
    const instance = await this.msalInstance();
    const response = await instance.handleRedirectPromise();
    const state = await this.state();

    if (response === null && state === null) {
      // Not sure if this is the right way to handle this, maybe we should have a logged out page?
      this.routerService.transitionTo('unauthorized');
      return;
    }

    if (response) {
      instance.setActiveAccount(response?.account);
    }

    await this.redirectToAttemptedURL();
  }

  async fetchAccessToken() {
    const state = await this.state();
    if (state && Object.keys(state).length) {
      const instance = await this.msalInstance();
      try {
        const response = await instance.acquireTokenSilent(this.authRequest);
        this.accessToken = response?.accessToken;
        this.tokenExpires = response.expiresOn;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          return instance.acquireTokenRedirect(this.authRequest);
        } else {
          console.error(error);
        }
      }
    }
  }

  setGuestToken(token: string) {
    this.guestToken = token;
  }

  clearTokens() {
    this.accessToken = undefined;
  }

  async unauthenticate() {
    return (await this.msalInstance()).logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}${config.msal.redirectUri}`,
    });
  }
}

function isMsalInteractionInProgressError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('interaction_in_progress');
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:session')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('session') declare altName: SessionService;`.
declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
